"use client";

import type {
  CareResourceImage,
  CareResourceStreamerLink,
} from "~www/lib/care-resources/types";

import { AudioPlayerCard } from "@homeinstead/ui/components/AudioPlayerCard";
import { useAudioPlayback } from "@homeinstead/ui/hooks/use-audio-playback";

type ArticleFeaturedAudioProps = {
  coverArt?: CareResourceImage;
  description?: string;
  durationSeconds?: number;
  src: string;
  streamerLinks: CareResourceStreamerLink[];
  title: string;
};

function isSpotifyLink(link: CareResourceStreamerLink): boolean {
  return /spotify/i.test(link.label);
}

function getStreamerMenuIcon(
  link: CareResourceStreamerLink,
): "external" | "spotify" {
  return isSpotifyLink(link) ? "spotify" : "external";
}

/**
 * The article page's featured episode player.
 *
 * Streamer links surface twice on purpose: the primary one becomes the card's
 * visible call to action, and the full set stays reachable from the options
 * menu. That replaces the separate "Listen on ..." list this block used to
 * render beneath the player.
 */
function ArticleFeaturedAudio({
  coverArt,
  description,
  durationSeconds,
  src,
  streamerLinks,
  title,
}: ArticleFeaturedAudioProps) {
  const {
    audioRef,
    currentTimeSeconds,
    durationSeconds: playbackDurationSeconds,
    hasError,
    isPlaying,
    isReady,
    seekToSeconds,
    skipBySeconds,
    togglePlayPause,
  } = useAudioPlayback({
    audioSource: src,
    initialDurationSeconds: durationSeconds,
  });

  async function handleCopyLink() {
    if (!navigator.clipboard) {
      return "Unable to copy link.";
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      return "Link copied.";
    } catch {
      return "Unable to copy link.";
    }
  }

  const primaryStreamerLink =
    streamerLinks.find(isSpotifyLink) ?? streamerLinks[0];

  return (
    <div>
      <audio preload="metadata" ref={audioRef} src={src} />
      <AudioPlayerCard
        audioPlayer={{
          ariaLabel: `Audio player: ${title}`,
          currentTimeSeconds,
          durationSeconds: playbackDurationSeconds,
          error: hasError ? "Audio playback is unavailable." : undefined,
          isLoading: !isReady && !hasError,
          isPlaying,
          menuActions: [
            ...streamerLinks.map((link) => ({
              href: link.url,
              icon: getStreamerMenuIcon(link),
              label: link.label,
            })),
            {
              icon: "copy" as const,
              label: "Copy link",
              onSelect: handleCopyLink,
            },
          ],
          onPlayPause: togglePlayPause,
          onSeek: seekToSeconds,
          onSkip: skipBySeconds,
          skipSeconds: 15,
        }}
        coverArt={coverArt}
        description={description}
        primaryActionLink={
          primaryStreamerLink
            ? {
                icon: isSpotifyLink(primaryStreamerLink)
                  ? "spotify"
                  : "generic",
                label: primaryStreamerLink.label,
                url: primaryStreamerLink.url,
              }
            : undefined
        }
        title={title}
        titleAs="p"
      />
    </div>
  );
}

export { ArticleFeaturedAudio };
